import React, { useState, useEffect } from 'react';
import api from '../services/api';
import useToast from '../hooks/useToast';
import ToastContainer from '../components/ToastContainer';

const DocumentsPage = () => {
  const [currentPath, setCurrentPath] = useState('/');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    file: null
  });
  const { toasts, showToast, removeToast } = useToast();

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await api.getDocuments();
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Dokümanlar yüklenirken hata:', error);
      // Veri yoksa örnek veri ekle
      await createSampleDocuments();
    } finally {
      setLoading(false);
    }
  };

  const createSampleDocuments = async () => {
    const sampleDocuments = [
      {
        name: "İnsan Kaynakları Politikaları",
        type: "folder",
        size: null,
        modified: new Date().toISOString(),
        icon: "bx bxs-folder",
        color: "text-warning"
      },
      {
        name: "Kalite Yönetim Sistemleri",
        type: "folder",
        size: null,
        modified: new Date().toISOString(),
        icon: "bx bxs-folder",
        color: "text-warning"
      },
      {
        name: "Proje Şablonları",
        type: "folder",
        size: null,
        modified: new Date().toISOString(),
        icon: "bx bxs-folder",
        color: "text-warning"
      },
      {
        name: "İş Sağlığı ve Güvenliği",
        type: "folder",
        size: null,
        modified: new Date().toISOString(),
        icon: "bx bxs-folder",
        color: "text-warning"
      },
      {
        name: "Yıllık İzin Prosedürü.pdf",
        type: "pdf",
        size: "1.2 MB",
        modified: new Date().toISOString(),
        icon: "bx bxs-file-pdf",
        color: "text-danger"
      },
      {
        name: "ISO_9001_Sertifikası.pdf",
        type: "pdf",
        size: "850 KB",
        modified: new Date().toISOString(),
        icon: "bx bxs-file-pdf",
        color: "text-danger"
      },
      {
        name: "Çalışan_El_Kitabı.docx",
        type: "word",
        size: "2.5 MB",
        modified: new Date().toISOString(),
        icon: "bx bxs-file-doc",
        color: "text-primary"
      },
      {
        name: "Organizasyon_Şeması.xlsx",
        type: "excel",
        size: "890 KB",
        modified: new Date().toISOString(),
        icon: "bx bxs-spreadsheet",
        color: "text-success"
      },
      {
        name: "Şirket_Sunumu.pptx",
        type: "powerpoint",
        size: "15.3 MB",
        modified: new Date().toISOString(),
        icon: "bx bxs-file-export",
        color: "text-orange"
      },
      {
        name: "Güvenlik_Politikası.pdf",
        type: "pdf",
        size: "675 KB",
        modified: new Date().toISOString(),
        icon: "bx bxs-file-pdf",
        color: "text-danger"
      }
    ];

    try {
      for (const doc of sampleDocuments) {
        await api.createDocument(doc);
      }
      setDocuments(sampleDocuments);
    } catch (error) {
      console.error('Örnek dokümanlar oluşturulurken hata:', error);
      setDocuments(sampleDocuments);
    }
  };

  const filteredDocuments = documents.filter(doc =>
    doc.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatFileSize = (size) => {
    if (!size) return '-';
    return size;
  };

  const getFileTypeLabel = (type) => {
    const types = {
      'folder': 'Klasör',
      'pdf': 'PDF Belgesi',
      'word': 'Word Belgesi',
      'excel': 'Excel Belgesi',
      'powerpoint': 'PowerPoint Sunumu',
      'image': 'Resim',
      'other': 'Diğer'
    };
    return types[type] || 'Bilinmeyen';
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    const iconMap = {
      'pdf': 'bx-file-pdf',
      'doc': 'bx-file-doc',
      'docx': 'bx-file-doc',
      'xls': 'bx-file',
      'xlsx': 'bx-file',
      'ppt': 'bx-file',
      'pptx': 'bx-file',
      'jpg': 'bx-image',
      'jpeg': 'bx-image',
      'png': 'bx-image',
      'gif': 'bx-image',
      'txt': 'bx-file-txt',
      'zip': 'bx-archive',
      'rar': 'bx-archive'
    };
    return iconMap[ext] || 'bx-file';
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadForm(prev => ({ ...prev, file }));
  };

  const handleUploadSubmit = async () => {
    if (!uploadForm.file) {
      showToast('Lütfen bir dosya seçin', 'error');
      return;
    }

    if (!uploadForm.title.trim()) {
      showToast('Lütfen dosya başlığı girin', 'error');
      return;
    }

    // Dosya boyutu kontrolü (50MB)
    if (uploadForm.file.size > 50 * 1024 * 1024) {
      showToast('Dosya boyutu 50MB\'dan büyük olamaz', 'error');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Progress simulation
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 100);

      console.log('Frontend Upload Debug:', {
        title: uploadForm.title,
        description: uploadForm.description,
        fileName: uploadForm.file?.name,
        titleType: typeof uploadForm.title,
        titleLength: uploadForm.title ? uploadForm.title.length : 0,
        titleTrimmed: uploadForm.title ? uploadForm.title.trim() : '',
        titleTrimmedLength: uploadForm.title ? uploadForm.title.trim().length : 0
      });

      const response = await api.uploadDocument(
        uploadForm.file, 
        uploadForm.title, 
        uploadForm.description
      );
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      // Yeni dosyayı listeye ekle
      setDocuments(prev => [response.data, ...prev]);
      
      // Form'u temizle
      setUploadForm({ title: '', description: '', file: null });
      setShowUploadModal(false);
      
      // Başarı mesajı
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
        showToast('Dosya başarıyla yüklendi!', 'success');
      }, 500);

    } catch (error) {
      console.error('Dosya yüklenirken hata:', error);
      showToast('Dosya yüklenirken hata oluştu: ' + error.message, 'error');
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDownload = async (documentId) => {
    try {
      const response = await api.downloadDocument(documentId);
      
      // Blob'u indir
      const blob = new Blob([response], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'document'; // Dosya adı
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Dosya indirilirken hata:', error);
      alert('Dosya indirilirken hata oluştu: ' + error.message);
    }
  };

  const handleDelete = async (documentId, documentName) => {
    showToast(
      `"${documentName}" dosyasını silmek istediğinizden emin misiniz?`,
      'warning',
      {
        duration: 0,
        actions: [
          {
            label: 'Evet, Sil',
            onClick: async () => {
              try {
                await api.deleteDocument(documentId);
                
                // Dosyayı listeden kaldır
                setDocuments(prev => prev.filter(doc => (doc._id || doc.id) !== documentId));
                
                showToast('Dosya başarıyla silindi!', 'success');
              } catch (error) {
                console.error('Dosya silinirken hata:', error);
                showToast('Dosya silinirken hata oluştu: ' + error.message, 'error');
              }
            }
          },
          {
            label: 'İptal',
            onClick: () => {}
          }
        ]
      }
    );
  };

  if (loading) {
    return (
      <div className="modern-loading">
        <div className="text-center">
          <div className="modern-spinner spinner-border mb-3" role="status">
            <span className="visually-hidden">Yükleniyor...</span>
          </div>
          <p className="text-muted">Dokümanlar yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="documents-page">
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      {/* Modern Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="modern-card p-4">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <h3 className="fw-bold text-dark mb-1">Doküman Merkezi</h3>
                <p className="text-muted mb-0">Şirket belgeleri ve dosya merkezi</p>
              </div>
              <div className="d-flex align-items-center gap-3">
                <div className="badge bg-primary rounded-pill px-3 py-2">
                  <i className="bx bx-folder me-1"></i>
                  {filteredDocuments.length} Doküman
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modern Toolbar */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="modern-card p-4">
            <div className="d-flex justify-content-between align-items-center">
              {/* Breadcrumb */}
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb modern-breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <button 
                      className="btn btn-link p-0 text-decoration-none"
                      onClick={() => setCurrentPath('/')}
                      style={{ color: '#2563eb' }}
                    >
                      <i className="bx bx-home me-1"></i> Ana Dizin
                    </button>
                  </li>
                  {currentPath !== '/' && (
                    <li className="breadcrumb-item active">Klasör</li>
                  )}
                </ol>
              </nav>

              {/* Search and Actions */}
              <div className="d-flex align-items-center gap-3">
                <div className="input-group" style={{width: '350px'}}>
                  <span className="input-group-text modern-search-icon">
                    <i className="bx bx-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control modern-search-input"
                    placeholder="Dokümanlarda ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <div className="d-flex align-items-center gap-2">
                  <button className="btn btn-modern-secondary px-3">
                    <i className="bx bx-filter me-1"></i>
                    Filtrele
                  </button>
                  <button className="btn btn-modern-secondary px-3">
                    <i className="bx bx-sort me-1"></i>
                    Sırala
                  </button>
                </div>

                <button 
                  className="btn btn-modern-primary px-4"
                  onClick={() => setShowUploadModal(true)}
                  disabled={uploading}
                >
                  <i className="bx bx-upload me-1"></i>
                  {uploading ? 'Yükleniyor...' : 'Dosya Yükle'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Progress */}
      {uploading && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="modern-card p-4">
              <div className="d-flex align-items-center">
                <div className="modern-spinner spinner-border-sm me-3" role="status">
                  <span className="visually-hidden">Yükleniyor...</span>
                </div>
                <div className="flex-grow-1">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="fw-medium">Dosya yükleniyor...</span>
                    <span className="text-muted">{uploadProgress}%</span>
                  </div>
                  <div className="progress" style={{ height: '6px' }}>
                    <div 
                      className="progress-bar bg-primary" 
                      role="progressbar" 
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modern List View - Default */}
      <div className="row">
        <div className="col-12">
          <div className="modern-card">
            <div className="p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0 modern-table">
                  <thead>
                    <tr>
                      <th className="modern-table-header">Dosya Adı</th>
                      <th className="modern-table-header">Tür</th>
                      <th className="modern-table-header">Boyut</th>
                      <th className="modern-table-header">Değiştirilme</th>
                      <th className="modern-table-header text-center">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDocuments.map((item) => (
                      <tr key={item._id || item.id} className="modern-table-row">
                        <td className="modern-table-cell">
                          <div className="d-flex align-items-center">
                            <div className="modern-file-icon me-3">
                              <i className={`${item.icon} ${item.color}`}></i>
                            </div>
                            <div>
                              <div className="fw-medium text-dark">{item.name}</div>
                              {item.description && (
                                <div className="text-muted small mt-1" style={{ fontSize: '0.75rem' }}>
                                  {item.description}
                                </div>
                              )}
                              <small className="text-muted">{getFileTypeLabel(item.type)}</small>
                            </div>
                          </div>
                        </td>
                        <td className="modern-table-cell">
                          <span className="modern-badge modern-badge-secondary">
                            {getFileTypeLabel(item.type)}
                          </span>
                        </td>
                        <td className="modern-table-cell text-muted">
                          {formatFileSize(item.size)}
                        </td>
                        <td className="modern-table-cell text-muted">
                          {new Date(item.modified).toLocaleDateString('tr-TR')}
                        </td>
                        <td className="modern-table-cell text-center">
                          <div className="modern-action-buttons">
                            <button 
                              className="btn btn-sm modern-btn-icon" 
                              title="İndir"
                              onClick={() => handleDownload(item._id || item.id)}
                            >
                              <i className="bx bx-download"></i>
                            </button>
                            <button 
                              className="btn btn-sm modern-btn-icon text-danger" 
                              title="Sil"
                              onClick={() => handleDelete(item._id || item.id, item.name)}
                              style={{ color: '#dc3545' }}
                            >
                              <i className="bx bx-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Modern Upload Modal */}
      {showUploadModal && (
        <div className="modern-modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modern-upload-modal" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="modern-upload-header">
              <div className="d-flex align-items-center">
                <div className="modern-upload-icon">
                  <i className="bx bx-cloud-upload"></i>
                </div>
                <div>
                  <h4 className="modern-upload-title mb-1">Dosya Yükle</h4>
                  <p className="modern-upload-subtitle">Yeni dosyanızı sisteme ekleyin</p>
                </div>
              </div>
              <button 
                className="modern-upload-close"
                onClick={() => setShowUploadModal(false)}
              >
                <i className="bx bx-x"></i>
              </button>
            </div>

            {/* Modal Body */}
            <div className="modern-upload-body">
              {/* File Drop Zone */}
              <div className="modern-drop-zone">
                <div className="modern-drop-content">
                  <div className="modern-drop-icon">
                    <i className="bx bx-cloud-upload"></i>
                  </div>
                  <h5 className="modern-drop-title">Dosyayı buraya sürükleyin</h5>
                  <p className="modern-drop-text">veya dosya seçmek için tıklayın</p>
                  <input
                    type="file"
                    className="modern-file-input"
                    onChange={handleFileUpload}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.txt,.zip,.rar"
                    id="fileInput"
                  />
                  <label htmlFor="fileInput" className="modern-file-label">
                    Dosya Seç
                  </label>
                </div>
                <div className="modern-drop-info">
                  <small className="text-muted">
                    <i className="bx bx-info-circle me-1"></i>
                    PDF, Word, Excel, PowerPoint, Resim, TXT, ZIP, RAR (Max: 50MB)
                  </small>
                </div>
              </div>

              {/* Selected File Preview */}
              {uploadForm.file && (
                <div className="modern-file-preview">
                  <div className="modern-file-card">
                    <div className="modern-file-icon-preview">
                      <i className={`bx ${getFileIcon(uploadForm.file.name)}`}></i>
                    </div>
                    <div className="modern-file-details">
                      <h6 className="modern-file-name">{uploadForm.file.name}</h6>
                      <p className="modern-file-size">{(uploadForm.file.size / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                    <button 
                      className="modern-file-remove"
                      onClick={() => setUploadForm(prev => ({ ...prev, file: null }))}
                    >
                      <i className="bx bx-x"></i>
                    </button>
                  </div>
                </div>
              )}

              {/* Form Fields */}
              <div className="modern-upload-form">
                <div className="modern-form-group">
                  <label className="modern-form-label">
                    <i className="bx bx-edit me-2"></i>
                    Dosya Başlığı *
                  </label>
                  <input
                    type="text"
                    className="modern-form-input"
                    placeholder="Dosya başlığını girin..."
                    value={uploadForm.title}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                  />
                </div>

                <div className="modern-form-group">
                  <label className="modern-form-label">
                    <i className="bx bx-message-square-detail me-2"></i>
                    Açıklama
                  </label>
                  <textarea
                    className="modern-form-textarea"
                    rows="3"
                    placeholder="Dosya hakkında açıklama..."
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                  ></textarea>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="modern-upload-footer">
              <button 
                className="modern-btn-cancel"
                onClick={() => setShowUploadModal(false)}
              >
                <i className="bx bx-x me-1"></i>
                İptal
              </button>
              <button 
                className="modern-btn-upload"
                onClick={handleUploadSubmit}
                disabled={uploading || !uploadForm.file || !uploadForm.title.trim()}
              >
                {uploading ? (
                  <>
                    <span className="modern-spinner me-2"></span>
                    Yükleniyor...
                  </>
                ) : (
                  <>
                    <i className="bx bx-upload me-1"></i>
                    Dosyayı Yükle
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modern Empty State */}
      {filteredDocuments.length === 0 && (
        <div className="row">
          <div className="col-12">
            <div className="modern-empty-state text-center py-5">
              <i className="bx bx-folder-open display-1 text-muted mb-4" style={{ color: '#e9ecef' }}></i>
              <h4 className="fw-bold text-dark mb-3">
                {searchTerm ? 'Arama sonucu bulunamadı' : 'Bu klasör boş'}
              </h4>
              <p className="text-muted mb-4">
                {searchTerm ? 'Aradığınız kriterlere uygun doküman bulunamadı.' : 'Henüz bu klasörde hiç dosya bulunmuyor.'}
              </p>
              <button 
                className="btn btn-modern-primary px-4"
                onClick={() => setShowUploadModal(true)}
              >
                <i className="bx bx-upload me-1"></i>
                {searchTerm ? 'Yeni Dosya Yükle' : 'İlk Dosyayı Yükleyin'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsPage; 